import { useTranslation } from"react-i18next";
import { useState, useEffect } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Badge } from"@/components/ui/badge";
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogFooter,
} from"@/components/ui/dialog";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from"@/components/ui/select";
import {
 Phone,
 User,
 Building2,
 DollarSign,
 Plus,
 Search,
 Edit,
 Trash2,
 Calendar,
 MapPin,
 FileText,
} from"lucide-react";
import { toast } from"sonner";

const ClientInquiriesPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [inquiries, setInquiries] = useState([]);
 const [projects, setProjects] = useState([]);
 const [units, setUnits] = useState([]);
 const [loading, setLoading] = useState(true);
 const [searchTerm, setSearchTerm] = useState("");
 const [showAddDialog, setShowAddDialog] = useState(false);
 const [editingInquiry, setEditingInquiry] = useState(null);
 const [saving, setSaving] = useState(false);

 // Form state
 const [formData, setFormData] = useState({
 client_name:"",
 phone_number:"",
 project_id:"",
 unit_id:"",
 price_type:"per_m2", // per_m2 or total
 price_per_m2:"",
 total_price:"",
 notes:"",
 status:"new", // new, followed_up, converted, lost
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 try {
 const [inquiriesRes, projectsRes] = await Promise.all([
 axios.get("/client-inquiries"),
 axios.get("/projects"),
 ]);
 setInquiries(inquiriesRes.data || []);
 setProjects(projectsRes.data || []);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const loadUnitsForProject = async (projectId) => {
 if (!projectId) {
 setUnits([]);
 return;
 }
 try {
 const response = await axios.get(`/units?project_id=${projectId}`);
 setUnits(response.data || []);
 } catch (error) {
 console.error("Failed to load units", error);
 }
 };

 const handleProjectChange = (projectId) => {
 setFormData({ ...formData, project_id: projectId, unit_id:""});
 loadUnitsForProject(projectId);
 };

 const resetForm = () => {
 setFormData({
 client_name:"",
 phone_number:"",
 project_id:"",
 unit_id:"",
 price_type:"per_m2",
 price_per_m2:"",
 total_price:"",
 notes:"",
 status:"new",
 });
 setUnits([]);
 setEditingInquiry(null);
 };

 const handleOpenAddDialog = () => {
 resetForm();
 setShowAddDialog(true);
 };

 const handleEditInquiry = async (inquiry) => {
 setEditingInquiry(inquiry);
 setFormData({
 client_name: inquiry.client_name ||"",
 phone_number: inquiry.phone_number ||"",
 project_id: inquiry.project_id ||"",
 unit_id: inquiry.unit_id ||"",
 price_type: inquiry.price_type ||"per_m2",
 price_per_m2: inquiry.price_per_m2 ||"",
 total_price: inquiry.total_price ||"",
 notes: inquiry.notes ||"",
 status: inquiry.status ||"new",
 });
 if (inquiry.project_id) {
 await loadUnitsForProject(inquiry.project_id);
 }
 setShowAddDialog(true);
 };

 const handleSaveInquiry = async () => {
 if (!formData.client_name || !formData.phone_number) {
 toast.error(t("validation.required"));
 return;
 }

 try {
 setSaving(true);
 const payload = {
 ...formData,
 price_per_m2: formData.price_per_m2 ? parseFloat(formData.price_per_m2) : null,
 total_price: formData.total_price ? parseFloat(formData.total_price) : null,
 };

 if (editingInquiry) {
 await axios.patch(`/client-inquiries/${editingInquiry.id}`, payload);
 toast.success(t("messages.updateSuccess"));
 } else {
 await axios.post("/client-inquiries", payload);
 toast.success(t("messages.saveSuccess"));
 }

 setShowAddDialog(false);
 resetForm();
 loadData();
 } catch (error) {
 toast.error(t("messages.saveFailed"));
 } finally {
 setSaving(false);
 }
 };

 const handleDeleteInquiry = async (inquiryId) => {
 if (!window.confirm("Are you sure you want to delete this inquiry?")) {
 return;
 }

 try {
 await axios.delete(`/client-inquiries/${inquiryId}`);
 toast.success(t("messages.deleteSuccess"));
 loadData();
 } catch (error) {
 toast.error(t("messages.deleteFailed"));
 }
 };

 const formatCurrency = (amount) => {
 if (!amount) return"-";
 return new Intl.NumberFormat("en-US", {
 style:"currency",
 currency:"EUR",
 minimumFractionDigits: 0,
 maximumFractionDigits: 0,
 }).format(amount);
 };

 const formatDate = (dateStr) => {
 try {
 return new Date(dateStr).toLocaleDateString("en-GB", {
 day:"numeric",
 month:"short",
 year:"numeric",
 });
 } catch {
 return dateStr;
 }
 };

 const getStatusBadge = (status) => {
 const styles = {
 new:"bg-blue-500/10 text-blue-700 border-blue-200/60",
 followed_up:"bg-amber-500/10 text-amber-700 border-yellow-200/60",
 converted:"bg-emerald-500/10 text-emerald-700 border-green-200/60",
 lost:"bg-rose-500/10 text-rose-700 border-red-200/60",
 };
 const labels = {
 new:"New",
 followed_up:"Followed Up",
 converted:"Converted",
 lost:"Lost",
 };
 return (
 <Badge className={`${styles[status] || styles.new} border`}>
 {labels[status] || status}
 </Badge>
 );
 };

 const getProjectName = (projectId) => {
 const project = projects.find((p) => p.id === projectId);
 return project?.name ||"General Inquiry";
 };

 const filteredInquiries = inquiries.filter(
 (inquiry) =>
 inquiry.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 inquiry.phone_number?.includes(searchTerm)
 );

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading...</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div className="flex justify-between items-start">
 <div>
 <h1
 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1"
 style={{ fontFamily:"Work Sans, sans-serif"}}
 >
 Client Inquiries
 </h1>
 <p className="text-slate-500">
 Track client calls and price quotes
 </p>
 </div>
 <Button
 onClick={handleOpenAddDialog}
 className="bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 data-testid="add-inquiry-btn"
 >
 <Plus className="w-4 h-4 mr-2"/>
 Add Inquiry
 </Button>
 </div>

 {/* Search */}
 <Card>
 <CardContent className="pt-6">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5"/>
 <Input
 placeholder="Search by client name or phone number..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10"
 data-testid="search-inquiries"
 />
 </div>
 </CardContent>
 </Card>

 {/* Stats */}
 <div className="grid grid-cols-4 gap-4">
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center gap-3">
 <Phone className="w-6 h-6 text-blue-600"/>
 <div>
 <p className="text-sm text-slate-500">Total Inquiries</p>
 <p className="text-2xl font-bold text-slate-900">
 {inquiries.length}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center gap-3">
 <User className="w-6 h-6 text-amber-600"/>
 <div>
 <p className="text-sm text-slate-500">New</p>
 <p className="text-2xl font-bold text-slate-900">
 {inquiries.filter((i) => i.status ==="new").length}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center gap-3">
 <DollarSign className="w-6 h-6 text-emerald-600"/>
 <div>
 <p className="text-sm text-slate-500">Converted</p>
 <p className="text-2xl font-bold text-slate-900">
 {inquiries.filter((i) => i.status ==="converted").length}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="flex items-center gap-3">
 <Trash2 className="w-6 h-6 text-rose-600"/>
 <div>
 <p className="text-sm text-slate-500">Lost</p>
 <p className="text-2xl font-bold text-slate-900">
 {inquiries.filter((i) => i.status ==="lost").length}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Inquiries List */}
 <Card>
 <CardHeader>
 <CardTitle>All Inquiries</CardTitle>
 </CardHeader>
 <CardContent>
 {filteredInquiries.length === 0 ? (
 <div className="text-center py-12 text-slate-500">
 <Phone className="w-12 h-12 mx-auto mb-4 text-slate-300"/>
 <p>No inquiries found</p>
 <p className="text-sm">
 Add your first client inquiry to start tracking
 </p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table
 className="w-full"
 data-testid="inquiries-table"
 >
 <thead>
 <tr className="border-b border-slate-200 text-left">
 <th className="pb-3 font-semibold text-slate-600">
 Client
 </th>
 <th className="pb-3 font-semibold text-slate-600">
 Project / Unit
 </th>
 <th className="pb-3 font-semibold text-slate-600 text-right">
 Price Offered
 </th>
 <th className="pb-3 font-semibold text-slate-600 text-center">
 Status
 </th>
 <th className="pb-3 font-semibold text-slate-600 text-center">
 Date
 </th>
 <th className="pb-3 font-semibold text-slate-600 text-center">
 Actions
 </th>
 </tr>
 </thead>
 <tbody>
 {filteredInquiries.map((inquiry) => (
 <tr
 key={inquiry.id}
 className="border-b border-white/40 hover:bg-white/80"
 >
 <td className="py-4">
 <div>
 <p className="font-medium text-slate-900 flex items-center gap-2">
 <User className="w-4 h-4 text-slate-400"/>
 {inquiry.client_name}
 </p>
 <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
 <Phone className="w-3 h-3"/>
 {inquiry.phone_number}
 </p>
 </div>
 </td>
 <td className="py-4">
 <div>
 <p className="font-medium text-[#334155] flex items-center gap-2">
 <Building2 className="w-4 h-4 text-slate-400"/>
 {getProjectName(inquiry.project_id)}
 </p>
 {inquiry.unit_id && (
 <p className="text-sm text-slate-500 mt-1">
 Unit: {inquiry.unit_name || inquiry.unit_id}
 </p>
 )}
 </div>
 </td>
 <td className="py-4 text-right">
 <div>
 {inquiry.price_per_m2 && (
 <p className="font-medium text-emerald-600">
 {formatCurrency(inquiry.price_per_m2)}/m²
 </p>
 )}
 {inquiry.total_price && (
 <p className="text-sm text-slate-600">
 Total: {formatCurrency(inquiry.total_price)}
 </p>
 )}
 {!inquiry.price_per_m2 && !inquiry.total_price && (
 <span className="text-slate-400">-</span>
 )}
 </div>
 </td>
 <td className="py-4 text-center">
 {getStatusBadge(inquiry.status)}
 </td>
 <td className="py-4 text-center text-sm text-slate-500">
 {formatDate(inquiry.created_at)}
 </td>
 <td className="py-4 text-center">
 <div className="flex items-center justify-center gap-2">
 <Button
 variant="ghost"
 size="sm"
 onClick={() => handleEditInquiry(inquiry)}
 data-testid={`edit-inquiry-${inquiry.id}`}
 >
 <Edit className="w-4 h-4 text-indigo-600"/>
 </Button>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => handleDeleteInquiry(inquiry.id)}
 data-testid={`delete-inquiry-${inquiry.id}`}
 >
 <Trash2 className="w-4 h-4 text-red-500"/>
 </Button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>
 </div>

 {/* Add/Edit Dialog */}
 <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
 <DialogContent className="max-w-lg">
 <DialogHeader>
 <DialogTitle>
 {editingInquiry ?"Edit Inquiry":"Add New Inquiry"}
 </DialogTitle>
 </DialogHeader>

 <div className="space-y-4">
 {/* Client Info */}
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Client Name *</Label>
 <Input
 value={formData.client_name}
 onChange={(e) =>
 setFormData({ ...formData, client_name: e.target.value })
 }
 placeholder="Enter client name"
 data-testid="input-client-name"
 />
 </div>
 <div>
 <Label>Phone Number *</Label>
 <Input
 value={formData.phone_number}
 onChange={(e) =>
 setFormData({ ...formData, phone_number: e.target.value })
 }
 placeholder="+389 7X XXX XXX"
 data-testid="input-phone-number"
 />
 </div>
 </div>

 {/* Project & Unit */}
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label>Project</Label>
 <Select
 value={formData.project_id ||"none"}
 onValueChange={(value) => handleProjectChange(value ==="none"?"": value)}
 >
 <SelectTrigger data-testid="select-project">
 <SelectValue placeholder="Select project"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="none">General Inquiry</SelectItem>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Unit (Optional)</Label>
 <Select
 value={formData.unit_id ||"none"}
 onValueChange={(value) =>
 setFormData({ ...formData, unit_id: value ==="none"?"": value })
 }
 disabled={!formData.project_id}
 >
 <SelectTrigger data-testid="select-unit">
 <SelectValue placeholder="Select unit"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="none">No specific unit</SelectItem>
 {units.map((unit) => (
 <SelectItem key={unit.id} value={unit.id}>
 {unit.name || unit.unit_number} ({unit.area_m2}m²)
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>

 {/* Price */}
 <div className="space-y-3">
 <Label>Price Offered</Label>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label className="text-sm text-slate-500">Price per m²</Label>
 <div className="relative">
 <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
 €
 </span>
 <Input
 type="number"
 value={formData.price_per_m2}
 onChange={(e) =>
 setFormData({ ...formData, price_per_m2: e.target.value })
 }
 placeholder="0"
 className="pl-8"
 data-testid="input-price-per-m2"
 />
 </div>
 </div>
 <div>
 <Label className="text-sm text-slate-500">Total Price</Label>
 <div className="relative">
 <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
 €
 </span>
 <Input
 type="number"
 value={formData.total_price}
 onChange={(e) =>
 setFormData({ ...formData, total_price: e.target.value })
 }
 placeholder="0"
 className="pl-8"
 data-testid="input-total-price"
 />
 </div>
 </div>
 </div>
 </div>

 {/* Status */}
 <div>
 <Label>Status</Label>
 <Select
 value={formData.status}
 onValueChange={(value) =>
 setFormData({ ...formData, status: value })
 }
 >
 <SelectTrigger data-testid="select-status">
 <SelectValue placeholder="Select status"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="new">New</SelectItem>
 <SelectItem value="followed_up">Followed Up</SelectItem>
 <SelectItem value="converted">Converted</SelectItem>
 <SelectItem value="lost">Lost</SelectItem>
 </SelectContent>
 </Select>
 </div>

 {/* Notes */}
 <div>
 <Label>Notes</Label>
 <textarea
 value={formData.notes}
 onChange={(e) =>
 setFormData({ ...formData, notes: e.target.value })
 }
 placeholder="Any additional notes about this inquiry..."
 className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
 data-testid="input-notes"
 />
 </div>
 </div>

 <DialogFooter>
 <Button
 variant="outline"
 onClick={() => {
 setShowAddDialog(false);
 resetForm();
 }}
 >
 Cancel
 </Button>
 <Button
 onClick={handleSaveInquiry}
 disabled={saving}
 className="bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 data-testid="save-inquiry-btn"
 >
 {saving ?"Saving...": editingInquiry ?"Update":"Add Inquiry"}
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default ClientInquiriesPage;
